'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Button } from '@acko/button'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import {
  ABHA_ANALYTICS_EVENTS,
  ABHA_ANALYTICS_JOURNEYS,
  buildAbhaAnalyticsProps,
} from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import {
  ABHA_HERO_WIDGET_DEFAULT_DATA,
  ABHA_HERO_WIDGET_BOX_SHADOW,
  ABHA_HERO_WIDGET_FORM_GRADIENT,
  ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON,
} from '@components/NewProduct/constants'
import { AbhaHeroModal } from '@components/NewProduct/AbhaHeroWidget/AbhaHeroModal'
import { AbhaHeroExistingAbhaModal } from '@components/NewProduct/AbhaHeroWidget/AbhaHeroExistingAbhaModal'
import { AbhaHeroModalStep } from '@components/NewProduct/AbhaHeroWidget/enums'
import { AbhaStickyFooter } from '@components/NewProduct/AbhaHeroWidget/AbhaStickyFooter'
import type { NewProductWidgetOptions } from 'src/mocks/newProductPage.mock'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import type { CSSProperties } from 'react'
import styles from './abhaHeroWidget.module.scss'

export type AbhaHeroWidgetProps = {
  widgetOptions?: NewProductWidgetOptions
  modalOptions?: Record<string, any>
  widgetCardImageUrl?: string
  segmentEventCommonProperties?: Record<string, any>
  searchParams?: Record<string, string>
  isStickyFooterVisible?: boolean
}

export const AbhaHeroWidget = ({
  widgetOptions,
  modalOptions = {},
  segmentEventCommonProperties = {},
  searchParams = {},
  isStickyFooterVisible = false,
}: AbhaHeroWidgetProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExistingAbhaModalOpen, setIsExistingAbhaModalOpen] = useState(false)
  const primaryCtaText = widgetOptions?.widgetPrimaryCtaText ?? ABHA_HERO_WIDGET_DEFAULT_DATA.CTA_TEXT
  const alreadyHaveAbhaText =
    widgetOptions?.widgetSecondaryCtaText ?? ABHA_HERO_WIDGET_DEFAULT_DATA.ALREADY_HAVE_ABHA_TEXT
  const stickyFooterData = widgetOptions?.stickyFooterData

  const handleCreateClick = (isBottomFloater = false) => {
    void (async () => {
      await trackEvent(ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET, {
        ...buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
          cta_text: primaryCtaText,
          funnel: 'entry',
        }),
        ...(isBottomFloater ? { widget_type: 'bottom floater' } : {}),
      })

      setIsModalOpen(true)
    })()
  }

  const handleExistingAbhaClick = (isBottomFloater = false) => {
    void (async () => {
      await trackEvent(ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET, {
        ...buildAbhaAnalyticsProps({
          journey: ABHA_ANALYTICS_JOURNEYS.EXISTING_ABHA_ENTRY,
          cta_text: alreadyHaveAbhaText,
          funnel: 'entry',
        }),
        ...(isBottomFloater ? { widget_type: 'bottom floater' } : {}),
      })
    })()
    setIsExistingAbhaModalOpen(true)
  }

  const cardStyle: CSSProperties & Record<string, string> = {
    '--abha-hero-widget-shadow': ABHA_HERO_WIDGET_BOX_SHADOW,
    '--abha-hero-widget-form-gradient': ABHA_HERO_WIDGET_FORM_GRADIENT,
  }

  return (
    <>
      <div id="AbhaRootContainer" className="flex w-full justify-center md:max-w-480 lg:w-auto lg:shrink-0">
        <div className={styles.widgetCard} style={cardStyle}>
          <div
            className={styles.widgetCardImage}
            role="img"
            aria-label="Sample ABHA cards"
            style={{
              backgroundImage: `url(${widgetOptions?.widgetCardImageSrc ?? ABHA_HERO_WIDGET_DEFAULT_DATA.IMAGE_SRC})`,
            }}
          />

          <div className={styles.widgetCardForm}>
            <div className={styles.widgetCardButtonGroup}>
              <Button id="abha-create-cta" variant="primary" size="lg" fullWidth onClick={() => handleCreateClick()}>
                {primaryCtaText}
              </Button>
              <Button
                id="abha-existing-cta"
                variant="ghost"
                size="md"
                className={styles.secondaryLink}
                onClick={() => handleExistingAbhaClick()}>
                <span>{alreadyHaveAbhaText}</span>
                <Image
                  src={ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON}
                  alt="Purple arrow"
                  width={16}
                  height={16}
                  aria-hidden
                  className={styles.secondaryLinkArrow}
                />
              </Button>
            </div>

            <PropertyControlledComponent controllerProperty={!!widgetOptions?.disclaimerText} fallback={null}>
              <div className={styles.disclaimerWrap}>
                <p className={styles.disclaimer}>{widgetOptions?.disclaimerText}</p>
              </div>
            </PropertyControlledComponent>
          </div>
        </div>
      </div>

      <PropertyControlledComponent controllerProperty={stickyFooterData} fallback={null}>
        <AbhaStickyFooter
          stickyFooterData={stickyFooterData}
          primaryCtaText={primaryCtaText}
          alreadyHaveAbhaText={alreadyHaveAbhaText}
          onCreateClick={() => handleCreateClick(true)}
          onExistingAbhaClick={() => handleExistingAbhaClick(true)}
          isVisible={isStickyFooterVisible}
        />
      </PropertyControlledComponent>

      <AbhaHeroModal
        open={isModalOpen}
        phone=""
        initialStep={AbhaHeroModalStep.Aadhaar}
        abhaSession={null}
        collectPhoneInAadhaarStep
        modalOptions={modalOptions}
        searchParams={searchParams}
        segmentEventCommonProperties={segmentEventCommonProperties}
        onClose={() => setIsModalOpen(false)}
      />

      <AbhaHeroExistingAbhaModal
        open={isExistingAbhaModalOpen}
        modalOptions={modalOptions}
        searchParams={searchParams}
        segmentEventCommonProperties={segmentEventCommonProperties}
        onClose={() => setIsExistingAbhaModalOpen(false)}
        onCreateAbha={() => {
          setIsExistingAbhaModalOpen(false)
          setIsModalOpen(true)
        }}
      />
    </>
  )
}
